import { z } from 'zod'

import type { RequestService } from '../request'
import { ruleSchema } from '@scalar-org/entities/rule'

/**
 * API for interacting with rules
 *
 * This is a wrapper around the core/v1/rules endpoints in the core service
 */
export function rulesApiFactory(requestService: RequestService) {
  /** Retrieve a managed ruleset content */
  async function get(namespace: string, slug: string) {
    return await requestService.request({
      url: `core/rulesets/${namespace}/${slug}`,
      method: 'get',
      schema: z.string(),
    })
  }

  /** Retrieve a managed ruleset content */
  async function getRules(namespace: string) {
    return await requestService.request({
      url: `core/rulesets/${namespace}`,
      method: 'get',
      schema: ruleSchema
        .pick({
          uid: true,
          title: true,
          description: true,
          slug: true,
          namespace: true,
          isPrivate: true,
        })
        .array(),
    })
  }

  /** Upload a new ruleset */
  async function create(
    namespace: string,
    data: {
      slug: string
      title: string
      description?: string
      isPrivate?: boolean
      document: string
    },
  ) {
    return await requestService.request({
      url: `core/rulesets/${namespace}`,
      method: 'post',
      data,
      schema: z.object({ uid: z.string() }),
    })
  }

  /** Update an existing rule contents */
  async function upload(data: { uid: string; data: string }) {
    return await requestService.request({
      url: 'core/rulesets/upload',
      method: 'post',
      data,
      schema: z.null(),
    })
  }

  async function update(
    namespace: string,
    slug: string,
    data: Partial<{
      namespace: string
      slug: string
      title: string
      description: string
      isPrivate: boolean
    }>,
  ) {
    return await requestService.request({
      url: `core/rulesets/${namespace}/${slug}`,
      method: 'PATCH',
      data,
      schema: z.null(),
    })
  }

  /** Delete a ruleset */
  async function del(namespace: string, slug: string) {
    return await requestService.request({
      url: `core/rulesets/${namespace}/${slug}`,
      method: 'delete',
      schema: z.null(),
    })
  }

  /** Access groups */
  async function addAccessGroup(data: {
    namespace: string
    slug: string
    groupUid: string
  }) {
    return await requestService.request({
      url: 'core/rules/access-group',
      method: 'post',
      data,
      schema: z.null(),
    })
  }

  async function removeAccessGroup(data: {
    namespace: string
    slug: string
    groupUid: string
  }) {
    return await requestService.request({
      url: 'core/rules/access-group',
      method: 'delete',
      data,
      schema: z.null(),
    })
  }

  /** Check if the slug is available */
  async function isRegistrySlugAvailable(namespace: string, slug: string) {
    return await requestService.request({
      url: 'core/rulesets/check-slug',
      method: 'post',
      data: {
        namespace,
        slug,
      },
      schema: z.object({
        available: z.boolean(),
      }),
    })
  }

  return {
    get,
    getRules,
    create,
    update,
    delete: del,
    upload,

    addAccessGroup,
    removeAccessGroup,

    isRegistrySlugAvailable,
  }
}
